import React from 'react';
import { useAuthStore } from 'src/store/auth';

import { deriveCoverage, useSelectionStore } from '../store/selection';
import { coverageLabel } from '../constants/categories';
import { QUOTE_COPY } from '../constants/copy';
import { categoryIcon } from '../constants/assets';
import { W2_SELECT_TID } from '../constants/testids';
import { money } from '../format';
import { W2SelectionStepLayout } from './W2SelectionStepLayout';

const TID = W2_SELECT_TID.quote;

interface QuoteReviewProps {
  onBack: () => void;
  onContinue: () => void;
  /** Bar position (STEP n / m). */
  step: number;
  total: number;
  /** Jump back to a category's plan screen ("Change Plan"). Omitted → no action. */
  onChangeCategory?: (category: string) => void;
}

interface DisplayRow {
  planId: number;
  shortName: string;
  category: string;
  elected: boolean;
  coverage: number | null;
  premium: number;
}

/**
 * Review of the elected set with figures, hosted in the shell (panel copy left,
 * plan summary card right). Per scope §Pricing (R7) / supervisor D1: the FULL
 * netted employee premium is shown per elected line plus the monthly total — and
 * NOTHING about employer contribution or any split. The server-computed quote
 * (`saveElections`) is the source of truth; the client-side coverage price is a
 * fallback so the screen still renders if the quote wire is sparse.
 */
export const QuoteReview: React.FC<QuoteReviewProps> = ({
  onBack,
  onContinue,
  step,
  total,
  onChangeCategory,
}) => {
  const context = useSelectionStore((s) => s.context);
  const lines = useSelectionStore((s) => s.lines);
  const quote = useSelectionStore((s) => s.quote);
  const firstName = useAuthStore((s) => s.user?.firstName);

  const offerings = context?.offerings ?? [];

  const premiumFromQuote = (planId: number): number | undefined =>
    quote?.lines.find((l) => l.planId === planId)?.premium;

  const dependents = context?.dependents ?? [];

  const rows: DisplayRow[] = offerings.map((offering) => {
    const line = lines[offering.planId];
    const elected = line?.decision === 'Elected';
    const coverage = elected
      ? deriveCoverage(line?.covered ?? [], dependents)
      : null;
    const clientPrice =
      coverage !== null
        ? (offering.coverageOptions.find((o) => o.coverage === coverage)
            ?.price ?? 0)
        : 0;
    return {
      planId: offering.planId,
      shortName: offering.shortName,
      category: offering.category,
      elected,
      coverage,
      premium: elected ? (premiumFromQuote(offering.planId) ?? clientPrice) : 0,
    };
  });

  const electedRows = rows.filter((r) => r.elected);
  const waivedRows = rows.filter((r) => !r.elected);

  const totalPremium =
    quote?.total ?? electedRows.reduce((sum, r) => sum + r.premium, 0);

  const grid =
    'md:grid md:grid-cols-[64px_1.6fr_1fr_120px] md:items-center md:gap-4';

  return (
    <W2SelectionStepLayout
      step={step}
      total={total}
      title={QUOTE_COPY.headline}
      body={<p>{QUOTE_COPY.body}</p>}
      rootTestId={TID.root}
      onPrev={onBack}
      prevLabel="Back to plans"
      prevTestId={TID.back}
      onNext={onContinue}
      nextLabel="NEXT"
      nextTestId={TID.continue}
    >
      <h2 className="text-2xl font-extrabold text-[#1a1a2e] max-sm:text-xl">
        Here is your plan, {firstName?.trim() || 'there'}!
      </h2>

      <div className="mt-6">
        <div
          className={`hidden border-b border-[#ececf2] pb-2 text-xs font-semibold uppercase tracking-wider text-[#8a8aa0] ${grid}`}
        >
          <span>Insurer</span>
          <span>Plan</span>
          <span>Actions</span>
          <span className="text-right">Monthly premium</span>
        </div>

        {electedRows.map((row) => {
          const icon = categoryIcon(row.category);
          return (
            <div
              key={row.planId}
              data-testid={`${TID.line}-${row.planId}`}
              className={`grid grid-cols-1 gap-1 border-b border-[#f0f0f5] py-3 ${grid}`}
            >
              <div className="flex items-center">
                {icon ? (
                  <img
                    src={icon}
                    alt=""
                    aria-hidden="true"
                    className="size-10 object-contain"
                  />
                ) : (
                  <span className="text-[13px] text-[#8a8aa0]">
                    {row.category}
                  </span>
                )}
              </div>
              <div>
                <p className="font-extrabold text-[#1a1a2e]">{row.shortName}</p>
                <p className="text-[13px] text-[#8a8aa0]">
                  {row.category}
                  {row.coverage !== null && (
                    <>
                      {' · '}
                      {coverageLabel(row.coverage)}
                    </>
                  )}
                </p>
              </div>
              <div className="text-[14px]">
                {onChangeCategory && (
                  <button
                    type="button"
                    onClick={() => onChangeCategory(row.category)}
                    className="font-semibold text-primary hover:underline"
                  >
                    Change Plan
                  </button>
                )}
              </div>
              <div className="text-[15px] font-semibold text-[#1a1a2e] md:text-right">
                {money(row.premium)}/mo
              </div>
            </div>
          );
        })}

        {electedRows.length === 0 && (
          <p className="border-b border-[#f0f0f5] py-4 text-[14px] text-[#5a5a70]">
            You waived all offered coverage.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[15px] font-semibold text-[#5a5a70]">
            Your monthly total
          </span>
          <span
            data-testid={TID.total}
            className="text-2xl font-extrabold text-[#1a1a2e]"
          >
            {money(totalPremium)}/mo
          </span>
        </div>
      </div>

      {waivedRows.length > 0 && (
        <div className="mt-8">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#8a8aa0]">
            Waived
          </p>
          <ul className="flex flex-col">
            {waivedRows.map((row) => (
              <li
                key={row.planId}
                data-testid={`${TID.lineWaived}-${row.planId}`}
                className="flex items-center justify-between border-b border-[#f0f0f5] py-2 text-[14px] last:border-b-0"
              >
                <span className="text-[#5a5a70]">
                  {row.shortName}{' '}
                  <span className="text-[#8a8aa0]">· {row.category}</span>
                </span>
                <span className="font-semibold text-[#8a8aa0]">Waived</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </W2SelectionStepLayout>
  );
};
